/**
 * @file Ekosystem.tsx
 * @description "Ekosystem / Aplikacje" — grouped inventory of the unified App/Project
 * entity (GET /api/ecosystem). Cards grouped by taxonomy (Vantage / Cybernetyka / Klienci
 * / Funkcje / Dev) with a live status dot; clicking a card opens its detail (/ekosystem/:id)
 * with the Trakcja profile + memory refs. Polls 60s. Copy is Polish (instance convention).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, RefreshCw, ChevronRight, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import type { EcosystemPayload, EcoStatus } from "../lib/api";

const POLL_MS = 60_000;

const DOT: Record<EcoStatus, string> = {
  live: "bg-emerald-400",
  stopped: "bg-red-400",
  unknown: "bg-gray-600",
};
const STATUS_LABEL: Record<EcoStatus, string> = {
  live: "live",
  stopped: "stop",
  unknown: "—",
};

function relAge(iso: string | null): string {
  if (!iso) return "brak odczytu live";
  const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return "przed chwilą";
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h} godz. temu` : `${Math.floor(h / 24)} dni temu`;
}

export function Ekosystem() {
  const [data, setData] = useState<EcosystemPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await api.ecosystem.get());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, POLL_MS);
    return () => clearInterval(i);
  }, [load]);

  const counts = useMemo(() => {
    const c = { live: 0, stopped: 0, unknown: 0 };
    for (const g of data?.groups ?? []) for (const it of g.items) c[it.status] += 1;
    return c;
  }, [data]);

  return (
    <div className="animate-fade-in space-y-8 max-w-4xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
            <Boxes className="w-4.5 h-4.5 text-accent" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-100">Ekosystem</h1>
            <p className="text-xs text-gray-500">
              Aplikacje i projekty · <span className="text-emerald-400">{counts.live} live</span> ·{" "}
              <span className="text-red-400">{counts.stopped} stop</span> · {counts.unknown} ?
              <span className="ml-2 font-mono text-gray-600">
                · {relAge(data?.generatedAt ?? null)}
              </span>
            </p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost flex-shrink-0" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Odśwież
        </button>
      </div>

      {error && !data && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse h-12 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      {data?.groups.map((g) =>
        g.items.length === 0 ? null : (
          <section key={g.key}>
            <h2
              className="text-sm font-semibold text-gray-100 mb-3 pl-2"
              style={{ borderLeft: `3px solid ${g.color}` }}
            >
              {g.label} <span className="text-gray-500 font-normal">· {g.items.length}</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {g.items.map((it) => (
                <Link
                  key={it.id}
                  to={`/ekosystem/${it.id}`}
                  className="card card-hover p-3 flex items-center gap-3 no-underline"
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[it.status]}`}
                    title={it.statusDetail || it.status}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-100 truncate">{it.name}</span>
                      {it.hasProfile && (
                        <span className="text-[9px] text-accent border border-accent/30 rounded px-1 py-0.5 flex-shrink-0">
                          profil
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-gray-500 truncate">
                      {it.vps?.vhost || it.vps?.pm2 || it.vps?.container || it.kind.toLowerCase()}
                    </span>
                  </span>
                  <span className="text-[10px] text-gray-500 flex-shrink-0">
                    {STATUS_LABEL[it.status]}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </section>
        )
      )}
    </div>
  );
}
